/** @jsxImportSource @emotion/react */

import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import TextBlock from '@component/common/block/text-block';
import Spinner from '@design-system/spinner';
import * as color from '@constant/color';
import * as sizeConstants from '@constant/size';
import { ETheme } from '@datatype/type';

type TBubbleSize = 'small' | 'medium' | 'large';
type TBubbleType = 'plain' | 'thinking' | 'typing' | 'loading';
type TAlign = 'left' | 'right';
// 꼬리 방향. 화자가 말풍선 위에 있으면 'top'을 쓴다.
type TTailDirection = 'side' | 'top';

interface IProps {
  type?: TBubbleType;
  text?: string;
  annotation?: { reading: string; color?: string };
  theme?: ETheme;
  size?: TBubbleSize;
  lineMaxWidth?: string;
  height?: string;
  align?: TAlign;
  tail?: TTailDirection;
  speakerName?: string;
  profileEmoji?: string;
  control?: React.ReactNode;
  animatedText?: boolean;
  // 타이핑 한 글자 노출 간격(ms). 미지정 시 기본값 사용.
  typingSpeed?: number;
  // 한 줄로 표시 + 넘치면 ellipsis(...)
  oneline?: boolean;
  // type='loading'일 때 Spinner 오른쪽에 붙일 문구. 없으면 Spinner만 돈다.
  loadingText?: string;
  handleClick?: () => void;
  handleTypingCompleted?: () => void;
}

const DOT_ANIMATION_INTERVAL = 200;
const CHAR_TYPING_INTERVAL = 100;

// thinking: 점 개수 3 → 4 → 5 → 4 → 3 → 4 ... 순환 (왕복)
const DOT_COUNT_PATTERN = [3, 4, 5, 4];
const useDotAnimation = (active: boolean) => {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!active) return;
    const timer = setInterval(() => setTick((t) => t + 1), DOT_ANIMATION_INTERVAL);
    return () => clearInterval(timer);
  }, [active]);
  const count = DOT_COUNT_PATTERN[tick % DOT_COUNT_PATTERN.length];
  return '·'.repeat(count).split('').join(' ');
};

/**
 * text를 앞에서부터 한 글자씩 드러내는 "타자기" 훅.
 *
 * 동작:
 * - isTyping=true이면 typedLength를 0부터 시작해 CHAR_TYPING_INTERVAL(ms) 간격으로 1씩 증가.
 * - typedLength가 text.length에 도달하면 타이머 정지 + handleEnd 콜백 호출.
 * - isTyping=false이면 즉시 전체 텍스트를 노출 (typedLength = text.length).
 * - text나 isTyping이 바뀌면 타이머를 리셋하고 처음부터 다시 타이핑.
 *
 * 반환값: 현재까지 노출할 substring (ex. "안녕하세요" 중 3글자면 "안녕하").
 */
const useTypewriter = (text: string, isTyping: boolean, intervalMs: number, handleCompleted?: () => void) => {
  // 지금까지 타이핑된 글자 수. 0 → 1 → 2 → ... → text.length 까지 증가.
  const [typedLength, setTypedLength] = useState(isTyping ? 0 : text.length);

  useEffect(() => {
    // 타이핑 애니메이션 안 씀 → 한 번에 전체 노출
    if (!isTyping) {
      setTypedLength(text.length);
      return;
    }

    // 0부터 다시 시작
    setTypedLength(0);

    // intervalMs 마다 한 글자씩 공개
    const timer = setInterval(() => {
      let isCompleted = false;
      setTypedLength((prev) => {
        if (prev >= text.length) {
          isCompleted = true;
          return prev;
        }
        return prev + 1;
      });
      if (isCompleted) {
        clearInterval(timer);
        handleCompleted?.();
      }
    }, intervalMs);

    // 언마운트 또는 의존성 변경 시 타이머 정리
    return () => clearInterval(timer);
  }, [isTyping, text, intervalMs]);

  return text.slice(0, typedLength);
};

const createDesign = (theme: ETheme, bubbleSize: TBubbleSize, align: TAlign, tail: TTailDirection) => {
  // ############### createSize ###############
  const createSize = () => {
    switch (bubbleSize) {
      case 'small':
        return {
          padding: '6px 10px',
          textSize: 'small' as const,
          tailSize: 6,
          borderRadius: sizeConstants.BORDER_RADIUS_1,
        };
      case 'large':
        return { padding: '14px 18px', textSize: 'large' as const, tailSize: 10, borderRadius: '14px' };
      case 'medium':
      default:
        return { padding: '10px 14px', textSize: 'medium' as const, tailSize: 8, borderRadius: '12px' };
    }
  };

  // ############### createTheme ###############
  const createTheme = () => {
    switch (theme) {
      case ETheme.BROWN:
        return { backgroundColor: color.BROWN_3, textColor: color.BLACK_2 };
      case ETheme.GRAY:
        return { backgroundColor: color.GRAY_4, textColor: color.BLACK_2 };
      case ETheme.YELLOW:
        return { backgroundColor: color.YELLOW_1, textColor: color.BLACK_2 };
      case ETheme.GREEN:
        return { backgroundColor: color.GREEN_5, textColor: color.GREEN_1 };
      case ETheme.RED:
        return { backgroundColor: color.RED_5, textColor: color.RED_1 };
      case ETheme.BLUE:
        return { backgroundColor: color.BLUE_5, textColor: color.BLUE_2 };
      case ETheme.BLACK:
        return { backgroundColor: color.BLACK_2, textColor: color.WHITE_1 };
      default:
        return { backgroundColor: color.GRAY_4, textColor: color.BLACK_2 };
    }
  };

  // ############### createTail ###############
  // 말풍선 꼬리 (좌측/우측). border 없이 단일 ::before 삼각형으로 구성.
  const createTail = (tailSize: number, backgroundColor: string) => {
    if (tail === 'top') {
      return `&::before {
        content: ''; position: absolute; width: 0; height: 0;
        top: -${tailSize}px; left: 18px;
        border-left: ${tailSize}px solid transparent;
        border-right: ${tailSize}px solid transparent;
        border-bottom: ${tailSize}px solid ${backgroundColor};
      }`;
    }

    const base = `content: ''; position: absolute; width: 0; height: 0; top: 10px;`;
    if (align === 'right') {
      return `&::before { ${base} right: -${tailSize}px;
        border-top: ${tailSize}px solid transparent;
        border-bottom: ${tailSize}px solid transparent;
        border-left: ${tailSize}px solid ${backgroundColor};
      }`;
    }
    return `&::before { ${base} left: -${tailSize}px;
      border-top: ${tailSize}px solid transparent;
      border-bottom: ${tailSize}px solid transparent;
      border-right: ${tailSize}px solid ${backgroundColor};
    }`;
  };

  const sizeDesign = createSize();
  const themeDesign = createTheme();

  return {
    padding: sizeDesign.padding,
    borderRadius: sizeDesign.borderRadius,
    textSize: sizeDesign.textSize,
    backgroundColor: themeDesign.backgroundColor,
    textColor: themeDesign.textColor,
    tailStyle: createTail(sizeDesign.tailSize, themeDesign.backgroundColor),
  };
};

interface IRowProps {
  $align: TAlign;
}

const Wrapper = styled.div<IRowProps>`
  display: flex;
  position: relative;
  flex-flow: row nowrap;
  align-items: flex-start;
  justify-content: ${(props) => (props.$align === 'right' ? 'flex-end' : 'flex-start')};
  width: 100%;
`;

interface IInnerProps {
  $maxWidth: string;
}

const InnerContainer = styled.div<IInnerProps>`
  display: flex;
  flex-flow: row nowrap;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
  max-width: ${(props) => props.$maxWidth};
`;

const ProfileCircle = styled.div`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: ${color.BLUE_5};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  line-height: 1;
`;

const ContentColumn = styled.div<IRowProps>`
  display: flex;
  flex-flow: column nowrap;
  align-items: ${(props) => (props.$align === 'right' ? 'flex-end' : 'flex-start')};
  min-width: 0;
  flex: 1;
`;

const SPEAKER_NAME_DESIGN = {
  text: { size: 'small', color: color.GRAY_1, bold: false },
  box: { alignX: 'left', margin: '0 0 5px 0' },
} as const;

interface IWrapperProps {
  $design: ReturnType<typeof createDesign>;
  $height: string;
}

const LineContainer = styled.div<IWrapperProps>`
  position: relative;
  box-sizing: border-box;
  width: fit-content;
  max-width: 100%;
  height: ${(props) => props.$height};
  padding: ${(props) => props.$design.padding};
  border-radius: ${(props) => props.$design.borderRadius};
  background-color: ${(props) => props.$design.backgroundColor};
  color: ${(props) => props.$design.textColor};
  line-height: 1.5;
  ${(props) => props.$design.tailStyle}
`;

// Spinner 하나만 들어가면 말풍선이 너무 좁다. 글자 한두 자가 들어갈 만큼 너비를 잡아 둔다.
// Spinner 하나만 들어가면 말풍선이 너무 좁다. 문구가 없을 때만 최소 너비를 잡는다.
const SpinnerContainer = styled.div<{ $hasText: boolean }>`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: ${({ $hasText }) => ($hasText ? 'flex-start' : 'center')};
  gap: 8px;
  width: ${({ $hasText }) => ($hasText ? 'auto' : '40px')};
`;

const textBlink = keyframes`
  0%, 100% {
    opacity: 0.8;
  }
  50% {
    opacity: 0.5;
  }
`;

const TextContainer = styled.div<{ $animated: boolean }>`
  animation: ${({ $animated }) => ($animated ? textBlink : 'none')} 1.5s ease-in-out infinite;
`;

const TextBubble: React.FC<IProps> = ({
  type = 'plain',
  text = '',
  annotation,
  theme = ETheme.BROWN,
  size = 'medium',
  lineMaxWidth = '100%',
  height = 'auto',
  align = 'left',
  tail = 'side',
  speakerName,
  profileEmoji,
  control,
  animatedText = false,
  typingSpeed = CHAR_TYPING_INTERVAL,
  oneline = false,
  loadingText,
  handleClick,
  handleTypingCompleted,
}) => {
  const design = createDesign(theme, size, align, tail);
  const isRight = align === 'right';
  const dots = useDotAnimation(type === 'thinking');
  const typed = useTypewriter(text, type === 'typing', typingSpeed, handleTypingCompleted);
  const displayText = type === 'thinking' ? dots : type === 'typing' ? typed : text;
  const isControlShow = typed.length === text.length;

  return (
    <Wrapper $align={align}>
      <InnerContainer $maxWidth={lineMaxWidth}>
        {!isRight && profileEmoji && <ProfileCircle>{profileEmoji}</ProfileCircle>}
        <ContentColumn $align={align}>
          {speakerName && <TextBlock text={speakerName} design={SPEAKER_NAME_DESIGN} />}
          <LineContainer $design={design} $height={height} onClick={handleClick}>
            {type === 'loading' ? (
              <SpinnerContainer $hasText={!!loadingText}>
                <Spinner size='16px' color={design.textColor} thickness='2px' />
                {loadingText && (
                  <TextBlock
                    text={loadingText}
                    design={{ text: { size: design.textSize, color: design.textColor }, box: { alignX: 'left' } }}
                  />
                )}
              </SpinnerContainer>
            ) : (
              <TextContainer $animated={animatedText}>
                <TextBlock
                  text={displayText}
                  annotation={annotation}
                  design={{
                    text: { size: design.textSize, color: design.textColor, oneline },
                    box: { alignX: 'left' },
                  }}
                />
              </TextContainer>
            )}
          </LineContainer>
          {isControlShow && control}
        </ContentColumn>
        {isRight && profileEmoji && <ProfileCircle>{profileEmoji}</ProfileCircle>}
      </InnerContainer>
    </Wrapper>
  );
};

export default TextBubble;
